"use client";

import Image from "next/image";
import { memo } from "react";
import { Link } from "@/i18n/navigation";
import { cn, FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { Typography } from "../Typography";
import { DiscoverItem } from "../home/sections/discover/discover.type";

export type DiscoverCardProps = {
  discover: DiscoverItem;
  className?: string;
};

const DiscoverCard = ({ discover, className }: DiscoverCardProps) => {
  const finalHref = `/discover/${discover.id}`;

  const cardContent = (
    <div className="relative flex h-[400px] w-full flex-col overflow-hidden rounded-2xl">
      <Image
        src={discover.image_url ?? FALLBACK_FEATURE_IMAGE}
        alt={discover.title ?? "N/A"}
        fill
        unoptimized
        className="object-cover transform-gpu transition-transform duration-500 ease-out group-hover:scale-[1.03]"
        sizes="(max-width: 640px) 100vw, 320px"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20" />

      <div className="relative z-20 mt-auto flex min-w-0 flex-col gap-1.5 px-3 pb-3">
        <Typography variant="subtitle1" className="line-clamp-2 text-white">
          {discover.title}
        </Typography>

        {/* Author */}
        <div className="flex min-w-0 items-center gap-2">
          <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full bg-muted">
            <Image
              src={discover.author.avatar_url}
              alt={discover.author.display_name}
              fill
              unoptimized
              className="object-cover"
            />
          </div>
          <div className="min-w-0">
            <Typography variant="subtitle2" className="truncate text-white">
              {discover.author.display_name}
            </Typography>
            <Typography variant="body-xs" className="text-white/70">
              {discover.created_at ?? "-"}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <article
      className={cn("group relative w-full rounded-2xl border shadow-product-card", className)}
    >
      {finalHref ? (
        <Link
          href={finalHref as never}
          className="block h-full"
          aria-label={`Open ${discover.title}`}
        >
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
    </article>
  );
};

export default memo(DiscoverCard);
